/**
 * Task Generation Hook
 * 
 * Automatically generates actionable tasks based on current system state.
 * Implements "Task-First, Not Feature-First" UX pattern.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from './use-transactions';
import { useAuth } from '@/contexts/auth-context';

export interface Task {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
  estimatedTime?: string;
  count?: number;
  icon: string;
  action: () => void;
  metadata?: {
    lastUpdate?: string;
    category?: 'upload' | 'review' | 'validate' | 'export' | 'fix';
  };
}

interface TaskGenerationOptions {
  includeCompleted?: boolean;
  maxTasks?: number;
}

/**
 * Generates smart task list based on current system state
 */
export const useTaskGeneration = (options: TaskGenerationOptions = {}) => {
  const { includeCompleted = false, maxTasks = 10 } = options;
  const navigate = useNavigate();
  const { organization } = useAuth();
  
  // Fetch current data
  const { data: transactionsData, isLoading } = useTransactions({
    page: 1,
    pageSize: 100,
    enabled: !!organization?.id,
  });

  const tasks: Task[] = useMemo(() => {
    if (isLoading || !transactionsData) return [];

    const generatedTasks: Task[] = [];
    const transactions = transactionsData.transactions || [];

    // Task 1: Upload (if no recent transactions or very few)
    const hasRecentTransactions = transactions.some(tx => {
      const txDate = new Date(tx.transaction_date);
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      return txDate > twoDaysAgo;
    });

    if (!hasRecentTransactions || transactions.length < 5) {
      const lastUploadDate = transactions[0]?.created_at 
        ? new Date(transactions[0].created_at).toLocaleDateString()
        : 'Never';
      
      generatedTasks.push({
        id: 'upload-transactions',
        title: 'Upload Documents',
        description: `Last upload: ${lastUploadDate}. Upload bank statements or receipts.`,
        priority: 'high',
        status: 'pending',
        icon: '📤',
        estimatedTime: '2 min',
        action: () => navigate('/file-processor'),
        metadata: {
          category: 'upload',
          lastUpdate: lastUploadDate,
        },
      });
    }

    // Task 2: Review AI Classifications (unclassified or low confidence)
    const needsReview = transactions.filter(tx => 
      !tx.human_verified ||
      (tx.ai_classification_confidence && tx.ai_classification_confidence < 70)
    );

    if (needsReview.length > 0) {
      const estimatedMinutes = Math.ceil(needsReview.length / 5);
      
      generatedTasks.push({
        id: 'review-classifications',
        title: 'Review AI Suggestions',
        description: `${needsReview.length} AI-classified transactions ready for your review`,
        priority: 'high',
        status: 'pending',
        icon: '🤖',
        count: needsReview.length,
        estimatedTime: `${estimatedMinutes} min`,
        action: () => navigate('/review-queue'),
        metadata: {
          category: 'review',
        },
      });
    }

    // Task 3: Fix Validation Errors (missing data, unbalanced, etc.)
    const hasValidationIssues = transactions.filter(tx => 
      !tx.account_number || 
      tx.swiss_vat_rate === null ||
      tx.classification_status === 'flagged'
    );

    if (hasValidationIssues.length > 0) {
      const issueTypes: string[] = [];
      const missingVAT = hasValidationIssues.filter(tx => tx.swiss_vat_rate === null).length;
      const missingAccount = hasValidationIssues.filter(tx => !tx.account_number).length;
      const flagged = hasValidationIssues.filter(tx => tx.classification_status === 'flagged').length;

      if (missingVAT > 0) issueTypes.push(`${missingVAT} missing VAT rates`);
      if (missingAccount > 0) issueTypes.push(`${missingAccount} missing accounts`);
      if (flagged > 0) issueTypes.push(`${flagged} flagged entries`);

      generatedTasks.push({
        id: 'fix-validation-errors',
        title: `Fix ${hasValidationIssues.length} Issues`,
        description: `Resolve validation errors:\n• ${issueTypes.join('\n• ')}`,
        priority: 'high',
        status: 'pending',
        icon: '⚠️',
        count: hasValidationIssues.length,
        action: () => navigate('/validation'),
        metadata: {
          category: 'fix',
        },
      });
    }

    // Task 4: Approve Ready Transactions
    const readyToApprove = transactions.filter(tx => 
      tx.classification_status === 'reviewed' ||
      (tx.ai_confidence_score && tx.ai_confidence_score >= 90 && !tx.human_verified)
    );

    if (readyToApprove.length > 0) {
      generatedTasks.push({
        id: 'approve-transactions',
        title: 'Validate & Approve',
        description: `${readyToApprove.length} transactions ready for approval`,
        priority: 'medium',
        status: 'pending',
        icon: '✅',
        count: readyToApprove.length,
        action: () => navigate('/transactions'),
        metadata: {
          category: 'validate',
        },
      });
    }

    // Task 5: Export to Accounting (if period complete)
    const approvedCount = transactions.filter(tx => 
      tx.classification_status === 'approved' ||
      tx.human_verified === true
    ).length;

    if (approvedCount >= 10) {
      const currentPeriod = getCurrentPeriod();
      
      generatedTasks.push({
        id: 'export-to-accounting',
        title: 'Export to Accounting',
        description: `${approvedCount} approved transactions ready for ${currentPeriod} export`,
        priority: 'low',
        status: 'pending',
        icon: '📊',
        count: approvedCount,
        action: () => navigate('/exports'),
        metadata: {
          category: 'export',
        },
      });
    }

    // Sort by priority (high > medium > low)
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    generatedTasks.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    // Limit number of tasks if specified
    return generatedTasks.slice(0, maxTasks);
  }, [transactionsData, isLoading, navigate, maxTasks]);

  // Calculate completion stats
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return {
    tasks,
    completedCount,
    totalCount,
    progress,
    isLoading,
    hasActiveTasks: tasks.some(t => t.status === 'pending'),
  };
};

/**
 * Helper: Get current period label (e.g., "Q4 2025")
 */
const getCurrentPeriod = (): string => {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return `Q${quarter} ${now.getFullYear()}`;
};
